import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavbar from '../../../components/TopNavbar';
import SideNavbar from '../../../components/SideNavbar';
import { useAuth } from '../../../AuthContext';
import { Resolution, Margin, Options, usePDF } from 'react-to-pdf';

// Firebase
import { db, auth, functions, storage } from '../../../firebaseConfig'; // Adjust the path based on your project structure
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, collection, query, where, orderBy, getDocs, addDoc, setDoc, limit, getCountFromServer } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { fetchInvoices, updateInvoice } from '../Admin/Function/AccountFunctions';

// redux
import { useAppSelector } from '../../../redux/store';
import { getDownloadURL, ref, StorageReference } from 'firebase/storage';
import getCurrentScreen from '../../../Helper/GetScreen';

// modals
import EmailModal from '../../../components/modals/EmailModal';
import Toast from '../../../components/Toast';


interface Invoice {
    id: string;
    data: {
        salutation: string;
        firstName: string;
        leadId: string; // Optional, if the invoice is linked to a lead
        invoiceNumber: string;
        invoiceDate: string;
        address: string;
        email: string;
        mobileNumber: string;
        notes: string;
        table: [
            {
                itemDetails: string,
                quantity: string,
                rate: string,
                tax: string,
                amount: string, // Includes tax
            },
        ], // Replace with proper table item type
        createdAt: string;
    };
}

export interface EmailAttachment {
    filename: string;
    content: string; // Base64 encoded content
    mimeType: string;
}

interface FetchInvoicesResponse {
    success: boolean;
    message: string;
    invoices: Invoice[];
    hasMore: boolean;
    lastDocument: {
        id: string;
        invoiceDate: string;
    } | null;
}

const Invoices = () => {
    const { userData, currentUser, isAdmin, isManager, isStaff } = useAuth();
    const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);

    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState("Invoice List");
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const popupRef = useRef<HTMLDivElement | null>(null);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
    const [invoiceStatuses, setInvoiceStatuses] = useState<{ [key: string]: string }>({});
    const [receivedAmounts, setReceivedAmounts] = useState<{ [key: string]: string }>({});

    const [isSending, setIsSending] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const statusOptions = ["Received", "Pending", "Overdue"];

    const [showCreateInvoice, setShowCreateInvoice] = useState(false);


    const [leadId, setLeadId] = useState<string>('');
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [allInvoices, setAllInvoices] = useState<Invoice[]>([]); // Store all fetched invoices
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [lastDocument, setLastDocument] = useState<{
        id: string;
        invoiceDate: string;
    } | null>(null);
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalInvoicesCount, setTotalInvoicesCount] = useState<number>(0);
    const [totalCountLoading, setTotalCountLoading] = useState<boolean>(true);
    const [pageSize] = useState<number>(10); // Fixed page size of 10
    const totalPages = Math.ceil(totalInvoicesCount / pageSize);

    const [dateRange, setDateRange] = useState({
        from: '',
        to: ''
    });


    const isCollapsed = useAppSelector(state => state.sidebar.isCollapsed);

    const handleFilterClick = () => setIsPopupOpen(!isPopupOpen);
    const handleOutsideClick = (event: MouseEvent) => {
        if (popupRef.current &&
            !popupRef.current.contains(event.target as Node)) {
            setIsPopupOpen(false);
        }
    };

    // functions
    const invoiceSettingsUpdate = httpsCallable(functions, 'invoiceSettingsUpdate');

    // Pagination navigation functions
    const handlePageChange = (pageNumber: number) => {
        // Use filtered pages for navigation limits
        const maxPages = Math.max(totalPages, 1);
        if (pageNumber < 1 || pageNumber > maxPages || loading) {
            return;
        }

        setCurrentPage(pageNumber);
    };

    // Function to load all invoices and cache them
    const loadAllInvoices = async () => {
        if (loading) return;

        setLoading(true);
        try {
            const auth = getAuth();
            let allFetchedInvoices: Invoice[] = [];
            let currentLastDoc = null;
            let fetchMore = true;

            // Fetch all invoices in larger batches for efficiency
            while (fetchMore) {
                const requestParams: any = {
                    pageSize: 500, // Increased batch size for faster loading of all invoices
                };

                if (currentLastDoc) {
                    requestParams.lastDocumentId = currentLastDoc.id;
                    requestParams.lastInvoiceDate = currentLastDoc.invoiceDate;
                }

                const result = await fetchInvoices(db, auth.currentUser, requestParams);
                const response = result.data as unknown as FetchInvoicesResponse;

                allFetchedInvoices = [...allFetchedInvoices, ...response.invoices];
                currentLastDoc = response.lastDocument;
                fetchMore = response.hasMore;
            }

            // Store all invoices and update display for current page
            setAllInvoices(allFetchedInvoices);
            updateDisplayedInvoices(allFetchedInvoices, currentPage);

            // Handle the received amount
            const receivedAmounts: { [key: string]: string } = {};
            const invoiceStatuses: { [key: string]: string } = {};

            setReceivedAmounts(receivedAmounts);
            setInvoiceStatuses(invoiceStatuses);

        } catch (error) {
            console.error('Failed to fetch all invoices:', error);
        } finally {
            setLoading(false);
        }
    };

    // Function to update displayed invoices - now shows all invoices
    const updateDisplayedInvoices = (allInvoicesData: Invoice[], pageNumber: number = 1) => {
        // Display all invoices without pagination
        setInvoices(allInvoicesData);
    };

    // Function to load invoices for a specific page
    const loadInvoicesForPage = async (pageNumber: number) => {
        // If we have all invoices cached, just slice and display
        if (allInvoices.length > 0) {
            updateDisplayedInvoices(allInvoices, pageNumber);
            return;
        }
        
        // Otherwise, fetch all invoices first
        await loadAllInvoices();
    };

    // Function to fetch total invoice count
    const fetchTotalInvoiceCount = async () => {
        try {
            setTotalCountLoading(true);
            const auth = getAuth();
            const currentUser = auth.currentUser;
            if (!currentUser) return;

            // Get user document to get companyId
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const userData = userDoc.data();
            if (!userData?.companyId) return;

            // Query to get total count
            const totalQuery = query(
                collection(db, `companies/${userData.companyId}/invoices`)
            );
            
            const totalSnapshot = await getCountFromServer(totalQuery);
            const totalCount = totalSnapshot.data().count;
            
            setTotalInvoicesCount(totalCount);
        } catch (error) {
            console.error('Failed to fetch total invoice count:', error);
        } finally {
            setTotalCountLoading(false);
        }
    };


    useEffect(() => {
        if (isPopupOpen) {
            document.addEventListener('mousedown', handleOutsideClick);
        } else {
            document.removeEventListener('mousedown', handleOutsideClick);
        }
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isPopupOpen]);
    const tableClass = "font-base font-normal p-2 text-zinc-100 font-Montserrat";
    const rowClass = "font-base font-normal p-2 text-neutral-600 text-center font-Montserrat";

    // Example usage
    async function loadInvoices(isLoadingMore = false, pageNumber = 1) {
        if (loading) return;

        setLoading(true);
        try {
            const auth = getAuth();
            
            // For page-based pagination, we don't use lastDocument
            // Instead, we let the backend handle page calculation
            const requestParams: any = {
                pageSize: pageSize,
                page: pageNumber // Add page parameter for backend
            };

            // Only use lastDocument for "load more" functionality (if still needed)
            if (isLoadingMore && lastDocument) {
                requestParams.lastDocumentId = lastDocument.id;
                requestParams.lastInvoiceDate = lastDocument.invoiceDate;
            }

            const result = await fetchInvoices(db, auth.currentUser, requestParams);
            const response = result.data as unknown as FetchInvoicesResponse;

            // For page navigation, replace all invoices; for load more, append
            setInvoices(prevInvoices =>
                isLoadingMore
                    ? [...prevInvoices, ...response.invoices]
                    : response.invoices
            );
            setHasMore(response.hasMore);

            // Update lastDocument for potential load more functionality
            setLastDocument(response.lastDocument);

            // Handle the received amount
            const receivedAmounts: { [key: string]: string } = {};
            const invoiceStatuses: { [key: string]: string } = {};

            setReceivedAmounts(receivedAmounts);
            setInvoiceStatuses(invoiceStatuses);

        } catch (error) {
            console.error('Failed to fetch invoices:', error);
        } finally {
            setLoading(false);
        }
    }

    // Initial load
    useEffect(() => {
        fetchTotalInvoiceCount();
        loadInvoicesForPage(currentPage);
    }, []);

    const loadMore = () => {
        if (!hasMore || loading) return;
        loadInvoices(true, currentPage);
    };

    const handleStatusChange = (invoiceId: string, orderId: string, newStatus: string) => {
        setInvoiceStatuses(prev => ({
            ...prev,
            [orderId]: newStatus
        }));

        // Update the status in the database
        const auth = getAuth();
        const updatedInvoice = updateInvoice(db, auth.currentUser, invoiceId, newStatus, undefined);

        // Handle the response
        updatedInvoice.then((response: any) => {
            console.log(response.message);
        }).catch((error: any) => {
            console.error('Failed to update invoice:', error);
        });

    };
    const handleReceivedChange = (invoiceId: string, orderId: string, receivedAmount: string) => {
        setReceivedAmounts(prev => ({
            ...prev,
            [orderId]: receivedAmount
        }));

        // Update the received amount in the database
        const auth = getAuth();
        const updatedInvoice = updateInvoice(db, auth.currentUser, invoiceId, undefined, parseFloat(receivedAmount));

        // Handle the response
        updatedInvoice.then((response: any) => {
            console.log(response.message);
        }).catch((error: any) => {
            console.error('Failed to update invoice:', error);
        });
    };


    // states for invoice creation
    const [salutation, setSalutation] = useState("");
    const [firstName, setFirstName] = useState("");
    const [isSalutationDropdownOpen, setIsSalutationDropdownOpen] = useState(false);
    const [isNameDropdownOpen, setIsNameDropdownOpen] = useState(false);
    const [students, setStudents] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isStudentLoading, setIsStudentLoading] = useState(false);
    const [address, setAddress] = useState("");
    const [email, setEmail] = useState("");
    const [mobileNumber, setMobileNumber] = useState("");
    const [invoiceNumber, setInvoiceNumber] = useState("");
    const [invoiceDate, setInvoiceDate] = useState("");
    const [table, setTable] = useState([
        {
            itemDetails: "",
            quantity: "0",
            rate: "0",
            tax: "0",
            amount: "0", // Includes tax
        },
    ]);
    const [notes, setNotes] = useState("");
    const [activeDropdown, setActiveDropdown] = useState<{ rowIndex: number | null, type: string | null }>({
        rowIndex: null,
        type: null
    });
    const inputRef = useRef<HTMLInputElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);

    const salutations = ["Mr.", "Ms.", "Mrs."];

    const handleDropdownToggle = (field: string) => {
        if (field === "salutation") {
            setIsSalutationDropdownOpen((prevState) => !prevState);
        }
        if (field === "name") {
            setIsNameDropdownOpen((prevState) => !prevState);
        }
    };

    const handleSelectSalutation = (value: string) => {
        setSalutation(value);
        setIsSalutationDropdownOpen(false);
    };

    // student search
    const handleStudentSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toUpperCase();
        setSearchTerm(value);

        // toogle student dropdown
        if (!isNameDropdownOpen) {
            handleDropdownToggle("name");
        }


        if (value.length < 3) {
            setStudents([]);
            return;
        }

        setIsStudentLoading(true);
        try {
            const currentUser = auth.currentUser;
            if (!currentUser) return;

            let companyId: string | undefined;
            // Get user document to get companyId
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            companyId = userDoc.data()?.companyId;
            if (!companyId) return;

            let studentsQuery = query(
                collection(db, `companies/${companyId}/leads`),
                where('name', '>=', value),
                where('name', '<=', value + '\uf8ff'),
                orderBy('name')
            );

            if (isManager && currentScreen === 'Manager') {
                const usersQuery = query(
                    collection(db, "users"),
                    where("companyId", "==", companyId),
                    where("managerIds", "array-contains", currentUser?.uid)
                );

                const usersSnapshot = await getDocs(usersQuery);
                const userIds = usersSnapshot.docs.map(doc => doc.id);
                studentsQuery = query(
                    collection(db, `companies/${companyId}/leads`),
                    where('name', '>=', value),
                    where('name', '<=', value + '\uf8ff'),
                    where('assignedTo', 'in', userIds),
                    orderBy('name')
                );
            } else if (isStaff && currentScreen === 'Staff') {
                studentsQuery = query(
                    collection(db, `companies/${companyId}/leads`),
                    where('name', '>=', value),
                    where('name', '<=', value + '\uf8ff'),
                    where('assignedTo', '==', currentUser?.uid),
                    orderBy('name')
                );
            }


            const studentsSnapshot = await getDocs(studentsQuery);
            const studentList = studentsSnapshot.docs.map((doc) => ({
                id: doc.id,
                name: doc.data().name,
                email: doc.data().email,
                number: doc.data().number,
            }));

            setStudents(studentList);
        } catch (error) {
            console.error("Error fetching students:", error);
        } finally {
            setIsStudentLoading(false);
        }
    };


    const useOutsideClick = (callback: () => void) => {
        const ref = useRef<HTMLDivElement>(null);

        useEffect(() => {
            const handleClick = (event: MouseEvent) => {
                if (ref.current && !ref.current.contains(event.target as Node)) {
                    callback();
                }
            };

            document.addEventListener('mousedown', handleClick);
            return () => {
                document.removeEventListener('mousedown', handleClick);
            };
        }, [callback]);

        return ref;
    };
    const salutationDropdownRef = useOutsideClick(() => {
        setIsSalutationDropdownOpen(false);
    });
    const nameDropdownRef = useOutsideClick(() => {
        setIsNameDropdownOpen(false);
    });

    const handleNumberTextToNumber = (value: string, key: keyof typeof table[number], index: number) => {
        // Allow only numbers and decimal points
        const numericValue = value.replace(/[^0-9.]/g, '');

        // Prevent multiple decimal points
        const parts = numericValue.split('.');
        const sanitizedValue = parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '');

        // Update the table with the new value and recalculate the amount in a single state update
        setTable((prev) => {
            const newTable = [...prev];

            // Update the changed field
            newTable[index][key] = sanitizedValue;

            // Get the most up-to-date values for calculation
            let rate = parseFloat(key === "rate" ? sanitizedValue : newTable[index].rate) || 0;
            let quantity = parseFloat(key === "quantity" ? sanitizedValue : newTable[index].quantity) || 0;
            let tax = parseFloat(key === "tax" ? sanitizedValue : newTable[index].tax) || 0;

            // Calculate the new amount
            const amount = (rate * quantity) + ((rate * quantity) * tax / 100);

            // Update the amount field
            newTable[index].amount = amount.toFixed(2);

            return newTable;
        });
    }

    // Function to apply filters to all invoices
    const applyFilters = (invoicesData: Invoice[]) => {
        return invoicesData.filter(invoice => {
            // Search term filter
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                const nameMatch = invoice.data.firstName?.toLowerCase().includes(searchLower);
                const invoiceMatch = invoice.data.invoiceNumber?.toLowerCase().includes(searchLower);
                if (!nameMatch && !invoiceMatch) return false;
            }

            // Date range filter
            if (dateRange.from || dateRange.to) {
                const invoiceDate = new Date(invoice.data.invoiceDate);
                const fromDate = dateRange.from ? new Date(dateRange.from) : null;
                const toDate = dateRange.to ? new Date(dateRange.to) : null;

                if (fromDate && invoiceDate < fromDate) return false;
                if (toDate && invoiceDate > toDate) return false;
            }

            return true;
        });
    };

    // Get filtered invoices - show all without pagination
    const filteredAllInvoices = applyFilters(allInvoices);
    const totalFilteredCount = filteredAllInvoices.length;
    
    // Show all filtered invoices instead of paginating
    const filteredInvoices = filteredAllInvoices;

    // Reset to page 1 when search term or date range changes
    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    }, [searchTerm, dateRange.from, dateRange.to]);


    const handleAddNewRow = () => {
        setTable((prev) => [...prev, {
            itemDetails: "",
            quantity: "0",
            rate: "0",
            tax: "",
            amount: "0"
        }]);
    }

    const handleRemoveRow = (index: number) => {
        setTable((prev) => prev.filter((_, i) => i !== index));
    }

    const handleGenerateInvoice = async () => {
        // Validate inputs
        if (!salutation || !firstName || !address || !mobileNumber || !invoiceNumber || !invoiceDate) {
            alert("Please fill in all required fields.");
            return;
        }

        if (table.length === 0) {
            alert("Please add at least one item to the invoice.");
            return;
        }

        // Validate table data
        for (const row of table) {
            if (!row.itemDetails || !row.quantity || !row.rate) {
                alert("Please fill in all item details.");
                return;
            }
        }

        // open invoice modal
        setInvoiceModalOpen(true);
    }

    const getInputContainerClass = (numColumns: number) => {
        switch (numColumns) {
            case 1:
                return "w-[30%]";
            case 2:
                return "grid grid-cols-2 gap-6 w-[70%]";
            case 3:
                return "grid grid-cols-3 gap-6 w-[90%]";
            default:
                return "w-3/4";
        }
    };
    const inputClass = "w-full py-3 px-4 bg-white border-0 rounded-3xl focus:outline-none focus:ring-2 focus:ring-blue-600  transition duration-200";
    const gradientBorderStyles = {
        background: 'linear-gradient(white, white) padding-box, linear-gradient(180deg, #3483BE 0%, #02385E 100%) border-box',
        border: '1px solid transparent',
        borderRadius: '24px'
    };
    const labelClass = "block font-montserrat font-normal text-base text-black mb-2";



    const [isLogoLoading, setIsLogoLoading] = useState(true);
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [companyLogo, setCompanyLogo] = useState<string | null>(null);
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [companyName, setCompanyName] = useState<string | null>(null);
    const [companyEmail, setCompanyEmail] = useState<string | null>(null);
    const [companyNumber, setCompanyNumber] = useState<string | null>(null);
    const [companyAddress, setCompanyAddress] = useState<string | null>(null);
    const [gstNumber, setGstNumber] = useState<string | null>(null);
    const [invoiceNumberPrefix, setInvoiceNumberPrefix] = useState<string | null>(null);
    const [termsAndConditions, setTermsAndConditions] = useState<string[]>([]);

    // modals
    const [openEmailModal, setOpenEmailModal] = useState(false);

    // temporary pdf data
    const [pdfDoc, setPdfDoc] = useState<EmailAttachment[]>();
    const [htmlContent, setHtmlContent] = useState<string | null>(null);

    // toast states
    const [toastMessage, setToastMessage] = useState('');
    const [isErrorToast, setIsErrorToast] = useState(false);
    const [showToast, setShowToast] = useState(false);

    useEffect(() => {
        const fetchCompanyId = async () => {
            const currentUser = auth.currentUser;
            if (!currentUser) return;

            let companyId: string | undefined;
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            companyId = userDoc.data()?.companyId;

            if (!companyId) return;
            setCompanyId(companyId || null);
            const companyDoc = await getDoc(doc(db, 'companies', companyId));
            if (companyDoc.exists()) {
                // fetch company details from invoiceSettings field in company document
                const companyData = companyDoc.data();
                setCompanyName(companyData?.invoiceSettings?.companyName || null);
                setCompanyEmail(companyData?.invoiceSettings?.companyEmail || null);
                setCompanyNumber(companyData?.invoiceSettings?.companyNumber || null);
                setCompanyAddress(companyData?.invoiceSettings?.companyAddress || null);
                setInvoiceNumberPrefix(companyData?.invoiceSettings?.invoiceNumberPrefix || null);
                setGstNumber(companyData?.invoiceSettings?.gstNumber || null);
                setTermsAndConditions(companyData?.invoiceSettings?.termsAndConditions || null);
            } else {
                console.log("Company not found");
            }
        };

        fetchCompanyId();
    }, []);

    useEffect(() => {
        const fetchCompanyLogo = async (companyId: string) => {
            setIsLogoLoading(true);
            try {
                const logoRef: StorageReference = ref(storage, `companies/logos/${companyId}.png`);
                const url = await getDownloadURL(logoRef);
                setCompanyLogo(url);
            } catch (error) {
                console.log("No company logo found or error fetching logo");
                setCompanyLogo(null);
            } finally {
                setIsLogoLoading(false);
            }
        };

        if (companyId) {
            fetchCompanyLogo(companyId);
        }
    }, [companyId]);

    useEffect(() => {
        // fetch the latest invoice and update the invoiceNumber by 1 and prefixing it with the invoiceNumberPrefix
        const fetchLatestInvoiceNumber = async () => {
            if (!companyId || !invoiceNumberPrefix) return;

            const invoicesQuery = query(
                collection(db, `companies/${companyId}/invoices`),
                orderBy('createdAt', 'desc'),
                limit(1)
            );

            const snapshot = await getDocs(invoicesQuery);
            if (snapshot.empty) {
                setInvoiceNumber(`${invoiceNumberPrefix}-0001`);
            } else {
                const latestInvoice = snapshot.docs[0].data();
                const latestInvoiceNumber = latestInvoice.invoiceNumber;

                // Check if the fetched invoice has the same prefix
                if (!latestInvoiceNumber.startsWith(invoiceNumberPrefix)) {
                    // Different prefix, start fresh with 0001
                    setInvoiceNumber(`${invoiceNumberPrefix}-0001`);
                } else {
                    // Same prefix, increment the number
                    const numberPart = parseInt(latestInvoiceNumber.replace(invoiceNumberPrefix + '-', ''), 10);
                    setInvoiceNumber(`${invoiceNumberPrefix}-${String(numberPart + 1).padStart(4, '0')}`);
                }
            }
        };

        if (companyId && invoiceNumberPrefix) {
            fetchLatestInvoiceNumber();
        }
    }, [companyId, invoiceNumberPrefix, showCreateInvoice]);


    // state to handle settings modal
    const [settingsModalOpen, setSettingsModalOpen] = useState(false);

    const handleTermsChange = (index: number, value: string) => {
        setTermsAndConditions((prev) => {
            const newTerms = [...prev];
            newTerms[index] = value;
            return newTerms;
        });
    }

    const handleAddTerm = () => {
        setTermsAndConditions((prev) => [...prev, ""]);
    }

    const handleRemoveTerm = (index: number) => {
        setTermsAndConditions((prev) => prev.filter((_, i) => i !== index));
    }

    const handleSettingsSave = async () => {
        console.log("Saving settings...", invoiceNumberPrefix);

        setSettingsLoading(true);
        
        try {
            const auth = getAuth();
            const companyId = auth.currentUser?.uid;

            if (!companyId) {
                setToastMessage('User not authenticated. Please log in again.');
                setIsErrorToast(true);
                setShowToast(true);
                setSettingsLoading(false);
                return;
            }

            const response = await invoiceSettingsUpdate({
                companyName,
                companyEmail,
                companyNumber,
                companyAddress,
                invoiceNumberPrefix,
                gstNumber,
                termsAndConditions,
            });

            if ((response.data as { success: boolean }).success) {
                console.log("Settings updated successfully");
                setSettingsModalOpen(false);
                setSettingsLoading(false);
                setToastMessage('Settings updated successfully!');
                setIsErrorToast(false);
                setShowToast(true);
            } else {
                setSettingsLoading(false);
                if (typeof response.data === 'object' && response.data !== null && 'message' in response.data) {
                    console.error("Error updating settings:", (response.data as { message: string }).message);
                    setToastMessage(`Failed to update settings: ${(response.data as { message: string }).message}`);
                } else {
                    console.error("Error updating settings: Unknown error");
                    setToastMessage('Failed to update settings. Please try again.');
                }
                setIsErrorToast(true);
                setShowToast(true);
            }
        } catch (error) {
            console.error("Error calling invoiceSettingsUpdate:", error);
            setToastMessage('Failed to update settings. Please check your connection and try again.');
            setIsErrorToast(true);
            setShowToast(true);
            setSettingsLoading(false);
        }
    }


    const options: Options = {
        // default is `save`
        method: 'save',
        // default is Resolution.MEDIUM = 3, which should be enough, higher values
        // increases the image quality but also the size of the PDF, so be careful
        // using values higher than 10 when having multiple pages generated, it
        // might cause the page to crash or hang.
        resolution: Resolution.NORMAL,
        page: {
            // margin is in MM, default is Margin.NONE = 0
            margin: Margin.SMALL,
            // default is 'A4'
            format: 'letter',
            // default is 'portrait'
            orientation: 'portrait',
        },
        canvas: {
            // default is 'image/jpeg' for better size performance
            mimeType: 'image/png',
            qualityRatio: 1,
            useCORS: true,
            logging: false,
        },
        // Customize any value passed to the jsPDF instance and html2canvas
        // function. You probably will not need this and things can break, 
        // so use with caution.
        overrides: {
            // see https://artskydj.github.io/jsPDF/docs/jsPDF.html for more options
            pdf: {
                compress: true,
                hotfixes: ['px_scaling'],
                unit: 'mm',
                format: 'letter',
                orientation: 'portrait',
                putOnlyUsedFonts: true,
                floatPrecision: 16, // default is 16, can be set to 2 for better performance
            },
            // see https://html2canvas.hertzen.com/configuration for more options
            canvas: {
                useCORS: true,
                allowTaint: true,
                foreignObjectRendering: false,
                scale: 2, // default is 2, can be set to 1 for better performance
            }
        },
    };

    const { toPDF, targetRef } = usePDF({ filename: `invoice_${invoiceNumber}.pdf`, ...options });

    const handleDownload = async () => {
        setIsDownloading(true);  // Changed from setIsSending to setIsDownloading
        try {
            if (showCreateInvoice) {
                await handleSaveInvoice();
            }

            // Generate the PDF
            await toPDF();

            // Show success message
            setToastMessage('Invoice downloaded successfully!');
            setIsErrorToast(false);
            setShowToast(true);

            // Reset the form
            setSalutation("");
            setFirstName("");
            setAddress("");
            setEmail("");
            setMobileNumber("");
            setInvoiceNumber("");
            setInvoiceDate("");
            setTable([
                {
                    itemDetails: "",
                    quantity: "0",
                    rate: "0",
                    tax: "0",
                    amount: "0", // Includes tax
                },
            ]);
            setNotes("");
            setInvoiceModalOpen(false);
            setShowCreateInvoice(false);
            setActiveSection("Invoice List");
            setInvoices([]);
            setAllInvoices([]); // Clear cached invoices
            setLastDocument(null);
            setHasMore(true);
            fetchTotalInvoiceCount();
            loadInvoicesForPage(1);
        } catch (error) {
            console.error("Error during download:", error);
            setToastMessage('Failed to download invoice. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
        } finally {
            setIsDownloading(false);  // Changed from setIsSending to setIsDownloading
        }
    };

    const handleDownloadFromList = async (invoice: Invoice) => {

        // populate the invoice data
        setLeadId(invoice.data.leadId);
        setSalutation(invoice.data.salutation);
        setFirstName(invoice.data.firstName);
        setAddress(invoice.data.address);
        setEmail(invoice.data.email);
        setMobileNumber(invoice.data.mobileNumber);
        setInvoiceNumber(invoice.data.invoiceNumber);
        setInvoiceDate(invoice.data.invoiceDate);
        setTable(invoice.data.table);
        setNotes(invoice.data.notes);
        setInvoiceModalOpen(true);

    }

    // File to base64 conversion utility
    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                // Extract the base64 part from the data URL
                const base64String = reader.result as string;
                // Remove the "data:mimetype;base64," prefix
                const base64Content = base64String.split(',')[1];
                resolve(base64Content);
            };
            reader.onerror = error => reject(error);
        });
    };

    // Handle send invoioce email
    const sendInvoiceEmail = async () => {
        setIsSending(true);
        if (!targetRef.current) {
            alert('Failed to generate PDF');
            return;
        }

        try {
            // // save the invoice data to the database
            // await handleSaveInvoice();

            const imageElement = document.querySelector('#company-logo') as HTMLImageElement | null;

            if (imageElement && !imageElement.complete) {
                await new Promise(resolve => {
                    imageElement.onload = resolve;
                    imageElement.onerror = resolve; // prevent hanging
                });
            }

            const jsPDF = (await import('jspdf')).jsPDF;
            const html2canvas = (await import('html2canvas')).default;

            // Get the element's computed styles to preserve layout
            const element = targetRef.current;
            const originalStyle = element.style.cssText;

            // Temporarily set fixed width and ensure all content is visible
            element.style.width = '794px'; // A4 width in pixels at 96 DPI
            element.style.maxWidth = '794px';
            element.style.overflow = 'visible';
            element.style.transform = 'scale(1)';
            element.style.transformOrigin = 'top left';

            // Wait for any dynamic content to load
            await new Promise(resolve => setTimeout(resolve, 100));

            // Enhanced html2canvas options for better quality
            const canvas = await html2canvas(element, {
                useCORS: true,
                allowTaint: true,
                scale: 3, // Higher scale for better quality
                width: element.scrollWidth,
                height: element.scrollHeight,
                scrollX: 0,
                scrollY: 0,
                windowWidth: element.scrollWidth,
                windowHeight: element.scrollHeight,
                backgroundColor: '#ffffff', // Ensure white background
                removeContainer: true,
                imageTimeout: 15000,
                logging: false,
                onclone: (clonedDoc) => {
                    // Ensure all fonts are loaded in cloned document
                    const clonedElement = clonedDoc.querySelector('[data-html2canvas-clone]') ||
                        clonedDoc.body.children[0];
                    if (clonedElement) {
                        // Force font rendering
                        (clonedElement as HTMLElement).style.fontFamily = window.getComputedStyle(element).fontFamily;
                        // Ensure all text is visible
                        const textElements = clonedElement.querySelectorAll('*');
                        textElements.forEach(el => {
                            const computedStyle = window.getComputedStyle(el);
                            (el as HTMLElement).style.fontSize = computedStyle.fontSize;
                            (el as HTMLElement).style.lineHeight = computedStyle.lineHeight;
                            (el as HTMLElement).style.fontWeight = computedStyle.fontWeight;
                        });
                    }
                }
            });

            // Restore original styles
            element.style.cssText = originalStyle;

            // Convert to high-quality PNG (better than JPEG for text)
            const imgData = canvas.toDataURL('image/png', 1.0);

            // Create PDF with exact A4 dimensions
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
                compress: false // Disable compression for better quality
            });

            const pageWidth = 210; // A4 width in mm
            const pageHeight = 297; // A4 height in mm
            const margin = 10; // 10mm margin

            // Calculate dimensions to maintain aspect ratio
            const availableWidth = pageWidth - (margin * 2);
            const availableHeight = pageHeight - (margin * 2);

            const canvasAspectRatio = canvas.width / canvas.height;
            const availableAspectRatio = availableWidth / availableHeight;

            let imgWidth, imgHeight;

            if (canvasAspectRatio > availableAspectRatio) {
                // Image is wider - fit to width
                imgWidth = availableWidth;
                imgHeight = availableWidth / canvasAspectRatio;
            } else {
                // Image is taller - fit to height
                imgHeight = availableHeight;
                imgWidth = availableHeight * canvasAspectRatio;
            }

            // Calculate position to top align the image
            const x = (pageWidth - imgWidth) / 2; // Center horizontally
            const y = margin; // Start from the top margin

            // Add image with high quality
            pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight, undefined, 'FAST');

            // Convert PDF to Blob
            const pdfBlob = pdf.output('blob');

            // Create File
            const pdfFile = new File([pdfBlob], `invoice_${invoiceNumber}.pdf`, {
                type: 'application/pdf',
            });

            // Convert to Base64
            const base64Content = await fileToBase64(pdfFile);

            // Email Attachment
            const attachment = {
                filename: `invoice_${invoiceNumber}.pdf`,
                content: base64Content,
                mimeType: 'application/pdf',
            };

            // Save attachment to state
            setPdfDoc([attachment]);

            // Open email modal
            setOpenEmailModal(true);

            // Show success message for PDF preparation
            setToastMessage('Invoice PDF generated successfully! Email composition window opened.');
            setIsErrorToast(false);
            setShowToast(true);

        } catch (error) {
            console.error('PDF generation failed:', error);
            setToastMessage('Failed to generate PDF. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
        } finally {
            setIsSending(false);
            await handleSaveInvoice(); // Save the invoice data to the database
        }
    };

    // save invoice data to the database
    const handleSaveInvoice = async () => {
        try {
            // Validate inputs
            if (!salutation || !firstName || !address || !mobileNumber || !invoiceNumber || !invoiceDate) {
                setToastMessage("Please fill in all required fields.");
                setIsErrorToast(true);
                setShowToast(true);
                return;
            }
            if (table.length === 0) {
                setToastMessage("Please add at least one item to the invoice.");
                setIsErrorToast(true);
                setShowToast(true);
                return;
            }
            // Validate table data
            for (const row of table) {
                if (!row.itemDetails || !row.quantity || !row.rate) {
                    setToastMessage("Please fill in all item details.");
                    setIsErrorToast(true);
                    setShowToast(true);
                    return;
                }
            }

            // Save the data to the database
            const auth = getAuth();
            const invoiceData = {
                leadId: leadId || "", // Optional, if the invoice is linked to a lead`
                salutation,
                firstName,
                address,
                email,
                mobileNumber,
                invoiceNumber,
                invoiceDate,
                table,
                notes: notes,
            };

            const invoiceRef = await addDoc(collection(db, `companies/${companyId}/invoices`), {
                ...invoiceData,
                createdAt: new Date(),
                createdBy: auth.currentUser?.uid,
            });

            console.log("Invoice created with ID: ", invoiceRef.id);

            // Reset the form and show success message
            setSalutation("");
            setFirstName("");
            setAddress("");
            setEmail("");
            setMobileNumber("");
            setInvoiceNumber("");
            setInvoiceDate("");
            setTable([
                {
                    itemDetails: "",
                    quantity: "0",
                    rate: "0",
                    tax: "0",
                    amount: "0", // Includes tax
                },
            ]);
            setNotes("");
            setInvoiceModalOpen(false);
            setShowCreateInvoice(false);
            setActiveSection("Invoice List");
            setInvoices([]);
            setLastDocument(null);
            setHasMore(true);
            loadInvoices();

            // Refresh total count
            fetchTotalInvoiceCount();

            // Show success message
            setToastMessage('Invoice saved successfully!');
            setIsErrorToast(false);
            setShowToast(true);

        } catch (error) {
            console.error("Error saving invoice:", error);
            setToastMessage('Failed to save invoice. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
        }
    };

    const handleCLose = () => {
        setInvoiceModalOpen(false);
        setShowCreateInvoice(false);
        setActiveSection("Invoice List");
        setSalutation("");
        setFirstName("");
        setAddress("");
        setEmail("");
        setMobileNumber("");
        setInvoiceNumber("");
        setInvoiceDate("");
        setTable([
            {
                itemDetails: "",
                quantity: "0",
                rate: "0",
                tax: "0",
                amount: "0", // Includes tax
            },
        ]);
        setNotes("");
        setLeadId("");
        setInvoices([]);
        setAllInvoices([]); // Clear cached invoices
        setLastDocument(null);
        setHasMore(true);
        setCurrentPage(1);
        fetchTotalInvoiceCount();
        loadInvoicesForPage(1);
    };


    return (
        <div className="min-h-screen bg-gray-200">
            {/* <div className="fixed top-0 left-0 w-full z-20">
                <TopNavbar />
            </div> */}
            <div className="fixed top-16 left-0 h-full z-10">
                <SideNavbar />
            </div>
            <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
                <div className="bg-white p-6 rounded-3xl shadow-md w-full min-h-screen flex flex-col">
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center gap-6">
                            <div className="flex items-center justify-center w-12 h-12 rounded-full"
                                style={{ background: "linear-gradient(180deg, #00A6D9 10%, #3483BE 100%)", }}>
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/desktop/basil_invoice-outline.svg`}
                                    alt="Icon"
                                    className="w-7 h-7" />
                            </div>
                            <div className="flex items-center space-x-4">
                                <h3 className="text-2xl font-semibold text-gray-900">Invoices</h3>
                                <div className="flex items-center">
                                    <span className="px-3 py-1 text-sm font-medium text-white rounded-full bg-gradient-to-r from-[#3483BE] to-[#02385E]">
                                        {totalCountLoading ? 'Loading...' : loading ? 'Loading all invoices...' : `Total: ${totalInvoicesCount} (All loaded)`}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center justify-between mb-6">
                        {!showCreateInvoice ? (
                            <div className="flex items-center px-6 gap-6">
                                {/* <h2 className="font-montserrat text-2xl font-medium-gray-900">Invoice List</h2> */}
                                <div className="flex items-center w-80 rounded-3xl bg-zinc-100">
                                    <img src={`${process.env.PUBLIC_URL}/images/desktop/searchbg.svg`} alt="search-icon" className="w-7 h-7 ml-2" />
                                    <input
                                        type="text"
                                        placeholder="Search by Name/Invoice #..."
                                        className="w-full px-4 py-2 bg-zinc-100 focus:outline-none rounded-3xl"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center px-6 gap-6">
                                <h2 className="font-montserrat text-2xl font-medium-gray-900"></h2>
                            </div>
                        )}

                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => setShowCreateInvoice(!showCreateInvoice)}
                                className="px-4 py-2 text-white font-semibold rounded-full flex items-center gap-2"
                                style={{ background: 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)', }} >
                                <span style={{ fontFamily: 'Montserrat', fontSize: '20px', fontWeight: '500', lineHeight: '24.38px', }}>
                                    {showCreateInvoice ? 'Invoice List' : 'Create Invoice'}
                                </span>
                                <img src={`${process.env.PUBLIC_URL}/images/desktop/staffadd.svg`} alt="Add Remittance Icon" className="w-4 h-4 ml-2" />
                            </button>
                            {!showCreateInvoice ? (
                                <div className="relative">
                                    <button
                                        onClick={handleFilterClick}
                                        className="text-gray-600 hover:text-gray-800 relative"
                                    >
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/desktop/NavFilter.svg`}
                                            alt="filter-icon"
                                            className="w-[35px] h-[35px]"
                                        />
                                        {(dateRange.from || dateRange.to) && (
                                            <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></span>
                                        )}
                                    </button>

                                    {isPopupOpen && (
                                        <div
                                            ref={popupRef}
                                            className="absolute top-0 right-0 transform translate-x-3 w-[300px] bg-white border rounded-3xl shadow-lg p-4 z-50"
                                        >
                                            <div className="flex justify-between items-center">
                                                <h3 className="text-[14px] font-normal">Date Range Filter</h3>
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/desktop/black-filter.svg`}
                                                    alt="filter-icon"
                                                    onClick={() => setIsPopupOpen(false)}
                                                    className="w-9 h-9 cursor-pointer"
                                                />
                                            </div>

                                            <div className="mt-4 space-y-4">
                                                <div>
                                                    <label className="text-[12px] font-normal block mb-1">From Date</label>
                                                    <input
                                                        type="date"
                                                        className="w-full p-2 border border-gray-300 rounded-lg"
                                                        value={dateRange.from}
                                                        onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                                                    />
                                                </div>

                                                <div>
                                                    <label className="text-[12px] font-normal block mb-1">To Date</label>
                                                    <input
                                                        type="date"
                                                        className="w-full p-2 border border-gray-300 rounded-lg"
                                                        value={dateRange.to}
                                                        onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                                                    />
                                                </div>
                                            </div>

                                            <div className="flex justify-between space-x-3 mt-6">
                                                <button
                                                    onClick={() => {
                                                        setDateRange({ from: '', to: '' });
                                                        setIsPopupOpen(false);
                                                    }}
                                                    className="px-8 py-2 text-white bg-black rounded-full text-sm"
                                                >
                                                    Clear
                                                </button>
                                                <button
                                                    onClick={() => setIsPopupOpen(false)}
                                                    className="px-8 py-2 text-white bg-cyan-600 rounded-full text-sm"
                                                >
                                                    Apply
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                // settings button
                                <div className="relative">
                                    <button onClick={() => setSettingsModalOpen(true)} className="text-gray-600 hover:text-gray-800">
                                        <img src={`${process.env.PUBLIC_URL}/images/desktop/settings-gear.svg`} alt="filter-icon" className="w-[35px] h-[35px]" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    {!showCreateInvoice && (
                        <div className="rounded-3xl overflow-hidden shadow-lg">
                            {/* Add max height and virtualization for better performance with large datasets */}
                            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                                <table className="min-w-full table-auto bg-zinc-100">
                                    <thead className="sticky top-0 z-10">
                                        <tr className="text-center bg-sky-950  ">
                                            <th className={tableClass}>Date</th>
                                            <th className={tableClass}>Invoice #</th>
                                            <th className={tableClass}>Name</th>
                                            <th className={tableClass}>Amount</th>
                                            <th className={tableClass}>Download Invoice</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredInvoices.map((invoice, index) => (
                                            <tr key={invoice.id || `invoice-${index}`} className="border-b">
                                                <td className={rowClass}>{invoice.data.invoiceDate}</td>
                                                <td className={rowClass}>{invoice.data.invoiceNumber}</td>
                                                <td className={rowClass}>{invoice.data?.firstName}</td>
                                                {/* <td className={rowClass}>
                                                    <div className="p-[2px] bg-gradient-to-b from-[#02385E] to-[#02385E] rounded-3xl">
                                                        <div className="relative">
                                                            <select
                                                                className="w-full py-2 pl-4 pr-10 bg-white text-gray-800 rounded-3xl focus:outline-none appearance-none"
                                                                value={invoiceStatuses[invoice.data.orderNumber] || "Pending"}
                                                                onChange={(e) => handleStatusChange(invoice.id, invoice.data.orderNumber, e.target.value)}
                                                            >
                                                                {statusOptions.map((status) => (
                                                                    <option key={status} value={status}>
                                                                        {status}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                                                <img
                                                                    src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`}
                                                                    alt="Dropdown Arrow"
                                                                    className="w-7 h-7 transition-transform duration-300"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td> */}
                                                <td className={rowClass}>{invoice?.data?.table.reduce((total: number, item: { amount: string; }) => total + (parseFloat(item.amount) || 0), 0).toFixed(2)}</td>
                                                {/* <td className={rowClass}>
                                                    <input
                                                        type="text"
                                                        className=" w-28 px-4 py-1 rounded-3xl border-2 border-sky-900 focus:outline-none focus:border-sky-500 text-center"
                                                        placeholder="Enter amount"
                                                        value={receivedAmounts[invoice.data.orderNumber] || ''}
                                                        onChange={(e) => handleReceivedChange(invoice.id, invoice.data.orderNumber, e.target.value)}
                                                    />
                                                </td> */}
                                                <td className="p-2 text-left">
                                                    <img
                                                        src={`${process.env.PUBLIC_URL}/images/desktop/tdesign_file-download.svg`}
                                                        alt="download-icon"
                                                        className="w-4 h-4 mx-auto cursor-pointer"
                                                        onClick={() => handleDownloadFromList(invoice)}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    
                    {/* Pagination Controls */}
                    {!showCreateInvoice && (
                        <div className="flex items-center justify-between mt-6 mb-4">
                            <div className="flex items-center gap-4">
                                <div className="text-xs text-gray-500">
                                    {loading ? 'Loading...' : 
                                        totalFilteredCount > 0 
                                            ? `Showing all ${totalFilteredCount} invoices${totalFilteredCount !== totalInvoicesCount ? ' (filtered)' : ''}`
                                            : 'No invoices found'
                                    }
                                </div>
                            </div>

                            {/* Navigation buttons - Hidden since showing all invoices */}
                            <div className="flex-1 flex items-right justify-end gap-2" style={{ display: 'none' }}>
                                {/* Pagination buttons hidden */}
                            </div>

                            {/* Page count - Hidden since showing all invoices */}
                            <div className="flex-1 text-right" style={{ display: 'none' }}>
                                {/* Page count hidden */}
                            </div>
                        </div>
                    )}
                    {showCreateInvoice && (
                        <div className="mt-6">
                            <h4 className="font-montserrat-normal text-xl  font-gray-900 mb-4">Address & Contact</h4>
                            <div className="mb-6">
                                <div className={getInputContainerClass(3)}>
                                    <div>
                                        <label htmlFor="salutation" className={labelClass}>Salutation <span className="text-red-500">*</span></label>
                                        <div className="relative">
                                            <div>
                                                <input ref={inputRef}
                                                    type="text"
                                                    value={salutation}
                                                    onClick={() => handleDropdownToggle("salutation")}
                                                    className={inputClass} style={gradientBorderStyles} placeholder="Select Salutation" />
                                            </div>
                                            <button type="button" className="absolute right-3 top-1/2 transform -translate-y-1/2" onClick={() => handleDropdownToggle("salutation")}>
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`} alt="Dropdown Arrow"
                                                    className={`w-7 h-7 transition-transform ${isSalutationDropdownOpen ? 'rotate-180' : ''}`} />
                                            </button>
                                            {isSalutationDropdownOpen && (
                                                <div ref={salutationDropdownRef} className="absolute top-full left-0 w-full mt-2 bg-white shadow-lg rounded-xl z-10">
                                                    {salutations.map((item) => (
                                                        <div key={item}
                                                            className="px-4 py-2 cursor-pointer hover:bg-gray-100" onClick={() => handleSelectSalutation(item)}>  {item}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="firstname" className={labelClass}>
                                            Name <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                ref={inputRef}
                                                type="text"
                                                id="firstname"
                                                placeholder="Gokul"
                                                value={searchTerm || firstName}
                                                className={inputClass}
                                                style={gradientBorderStyles}
                                                onChange={handleStudentSearch}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (!isNameDropdownOpen) {
                                                        handleDropdownToggle("name");
                                                    }
                                                }}
                                            />

                                            {/* Clear (X) Button */}
                                            {(searchTerm || firstName) && (
                                                <button
                                                    type="button"
                                                    className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500"
                                                    onClick={() => {
                                                        setSearchTerm('');
                                                        setFirstName('');
                                                        setLeadId('');
                                                        setEmail('');
                                                        setMobileNumber('');
                                                        setIsNameDropdownOpen(false);
                                                    }}
                                                    title="Clear selection"
                                                >
                                                    &#x2715;
                                                </button>
                                            )}

                                            {/* Dropdown Toggle Arrow */}
                                            <button
                                                type="button"
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2"
                                                onClick={() => handleDropdownToggle("name")}
                                            >
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`}
                                                    alt="Dropdown Arrow"
                                                    className={`w-7 h-7 transition-transform ${isNameDropdownOpen ? 'rotate-180' : ''}`}
                                                />
                                            </button>

                                            {/* Dropdown */}
                                            {isNameDropdownOpen && (
                                                <div
                                                    ref={nameDropdownRef}
                                                    className="absolute z-10 bg-white border border-gray-300 rounded-lg shadow-lg mt-1 w-full max-h-[200px] overflow-y-auto"
                                                >
                                                    {isStudentLoading ? (
                                                        <div className="absolute top-0 right-0 mt-2 mr-2">
                                                            <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                                        </div>
                                                    ) : students.length > 0 ? (
                                                        students.map((student) => (
                                                            <div
                                                                key={student.id}
                                                                className="p-2 hover:bg-gray-100 cursor-pointer"
                                                                onClick={() => {
                                                                    setLeadId(student?.id);
                                                                    setFirstName(student?.name);
                                                                    setEmail(student?.email);
                                                                    setMobileNumber(student?.number);
                                                                    setSearchTerm('');
                                                                    setIsNameDropdownOpen(false);
                                                                }}
                                                            >
                                                                {student.name}
                                                            </div>
                                                        ))
                                                    ) : searchTerm.length < 3 ? (
                                                        <div className="p-2 text-gray-500">Type at least 3 characters to search</div>
                                                    ) : (
                                                        <div className="p-2 text-gray-500">No students found</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="number" className={labelClass}>Mobile Number <span className="text-red-500">*</span></label>
                                        <input type="text" id="number" value={mobileNumber} placeholder="+91-1234567890" className={inputClass} style={gradientBorderStyles} onChange={(e) => setMobileNumber(e.target.value)} />
                                    </div>
                                    <div>
                                        <label htmlFor="email" className={labelClass}>Email Address</label>
                                        <input type="text" id="email" value={email} placeholder="gklajit@gmail.com" className={inputClass} style={gradientBorderStyles} onChange={(e) => setEmail(e.target.value)} />
                                    </div>
                                    <div>
                                        <label htmlFor="invoiceDate" className={labelClass}>Invoice Number <span className="text-red-500">*</span></label>
                                        <input type="text" id="invoiceDate" value={invoiceNumber} placeholder="SCRD-2024-0019" className={inputClass} style={gradientBorderStyles} onChange={(e) => setInvoiceNumber(e.target.value)} />
                                    </div>
                                    <div>
                                        <label htmlFor="invoiceDate" className={labelClass}>Invoice Date <span className="text-red-500">*</span></label>
                                        <input type="date" id="invoiceDate" value={invoiceDate} placeholder="25/11/24" className={inputClass} style={gradientBorderStyles} onChange={(e) => setInvoiceDate(e.target.value)} />
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4">
                                <label htmlFor="Subject" className={labelClass}>Address <span className="text-red-500">*</span></label>
                                <textarea
                                    id="address"
                                    rows={4}
                                    value={address}
                                    className={`${inputClass} w-[80%] h-22 resize-none`}
                                    style={gradientBorderStyles}
                                    placeholder="Enter Address of the customer"
                                    onChange={(e) => setAddress(e.target.value)}
                                />
                            </div>

                            <div className="mt-4">
                                <label htmlFor="Subject" className={labelClass}>Note</label>
                                <textarea
                                    id="notes"
                                    rows={4}
                                    value={notes}
                                    className={`${inputClass} w-[80%] h-22 resize-none`}
                                    style={gradientBorderStyles}
                                    placeholder="Let your customer know what this invoice is for "
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                            </div>

                            <div className="mt-6">
                                <div className="rounded-3xl overflow-hidden bg-zinc-100 shadow-lg">
                                    <div className="flex justify-between bg-sky-950  text-white items-center px-6 py-2">
                                        <h4 className="font-montserrat text-lg text-white">Break Up</h4>
                                        {/* <div className="flex gap-2">
                                            <button className="flex items-center gap-2 py-2 px-4 rounded-3xl bg-transparent  text-[#3483BE]">
                                                <img src={`${process.env.PUBLIC_URL}/images/desktop/lucide_scan-line.svg`}
                                                    alt="Icon"
                                                    className="w-5 h-5" />
                                                <span className="text-white underline decoration-1 underline-offset-2">Scan Item</span>
                                            </button>
                                            <button className="flex items-center gap-2 py-2 px-4 rounded-3xl bg-transparent  text-[#3483BE]">
                                                <img src={`${process.env.PUBLIC_URL}/images/desktop/mage_checklist-note.svg`}
                                                    alt="Icon"
                                                    className="w-5 h-5" />
                                                <span className="text-white underline decoration-1 underline-offset-2">Bulk Actions</span>
                                            </button>
                                        </div> */}
                                    </div>

                                    <div className="overflow-x-auto relative">
                                        <table className="min-w-full table-auto bg-zinc-100">
                                            <thead>
                                                <tr className="text-center bg-zinc-300 text-neutral-600">
                                                    <th className="p-2">Item Details <span className="text-red-500">*</span></th>
                                                    <th className="p-2">Quantity <span className="text-red-500">*</span></th>
                                                    <th className="p-2">Rate <span className="text-red-500">*</span></th>
                                                    <th className="p-2">Tax (%)</th>
                                                    <th className="p-2">Amount</th>
                                                    <th className="p-2"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {table.map((data, index) => (
                                                    <tr key={index} className="border-b">
                                                        <td className="p-2">
                                                            <div className="relative dropdown-container" >
                                                                <input
                                                                    type="text"
                                                                    value={data.itemDetails}
                                                                    onChange={(e) => setTable((prev) => {
                                                                        const newTable = [...prev];
                                                                        newTable[index].itemDetails = e.target.value;
                                                                        return newTable;
                                                                    })}
                                                                    className="w-full py-3 px-4 bg-white border border-black rounded-3xl transition duration-200"
                                                                    placeholder="Enter Item Details"
                                                                />
                                                            </div>
                                                        </td>
                                                        <td className="p-2">
                                                            <input
                                                                type="text"
                                                                value={data.quantity}
                                                                className="w-full py-3 px-4 bg-white border border-black rounded-3xl transition duration-200 text-center"
                                                                placeholder="0.00"
                                                                onChange={(e) => handleNumberTextToNumber(e.target.value, 'quantity', index)}
                                                            />
                                                        </td>
                                                        <td className="p-2">
                                                            <input
                                                                type="text"
                                                                value={data.rate}
                                                                className="w-full py-3 px-4 bg-white border border-black rounded-3xl transition duration-200 text-center"
                                                                placeholder="0.00"
                                                                onChange={(e) => handleNumberTextToNumber(e.target.value, 'rate', index)}
                                                            />
                                                        </td>
                                                        <td className="p-2">
                                                            <input
                                                                type="text"
                                                                value={data.tax}
                                                                className="w-full py-3 px-4 bg-white border border-black rounded-3xl transition duration-200 text-center"
                                                                placeholder="0.00"
                                                                onChange={(e) => handleNumberTextToNumber(e.target.value, 'tax', index)}
                                                            />
                                                        </td>
                                                        <td className="p-2">
                                                            <input
                                                                type="number"
                                                                value={data.amount}
                                                                className="w-full bg-transparent border-0 focus:outline-none text-center"
                                                                placeholder="0.00"
                                                                readOnly
                                                            />
                                                        </td>
                                                        <td className="p-2 text-center">
                                                            <div className="flex justify-center gap-4">
                                                                <img
                                                                    src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                                                                    className="w-5 h-5 cursor-pointer"
                                                                    alt="Delete Icon"
                                                                    onClick={() => handleRemoveRow(index)}
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        <div className="px-6">
                                            <hr className="mt-0 mb-6 w-full" style={{ border: "none", height: "2px", background: "linear-gradient(to right, #FFFFFF 0%, #72AAD2 33%, #5E9DCB 66%, #FFFFFF 100%)" }} />
                                        </div>

                                        <div className="flex justify-start px-6 pb-6 gap-4">
                                            <button className="px-4 py-2 text-white font-semibold rounded-full flex items-center gap-2"
                                                style={{ background: 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)', }}
                                                onClick={() => handleAddNewRow()}
                                            >
                                                <span style={{ fontFamily: 'Montserrat', fontSize: '20px', fontWeight: '500', lineHeight: '24.38px', }}>
                                                    Add New Row
                                                </span>
                                                <img src={`${process.env.PUBLIC_URL}/images/desktop/staffadd.svg`} alt="Add Staff Icon" className="w-4 h-4 ml-2" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 flex justify-end items-end">
                                <div className="flex gap-4">
                                    <button className="px-4 py-2 text-white font-semibold rounded-full flex items-center gap-2 "
                                        style={{ background: 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)', }} onClick={() => handleGenerateInvoice()}>
                                        <span style={{ fontFamily: 'Montserrat', fontSize: '20px', fontWeight: '500', lineHeight: '24.38px', }}>
                                            Generate Invoice
                                        </span>
                                        <img src={`${process.env.PUBLIC_URL}/images/desktop/staffadd.svg`} alt="Add Staff Icon" className="w-4 h-4 ml-2" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    <hr className="mt-8 mb-6 w-full" style={{ border: "none", height: "2px", background: "linear-gradient(to right, #FFFFFF 0%, #72AAD2 33%, #5E9DCB 66%, #FFFFFF 100%)", }} />
                </div>
            </div>

            {invoiceModalOpen && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-4 md:p-6 lg:px-10 rounded-3xl w-full max-w-4xl mx-auto h-[calc(100vh-130px)] relative overflow-hidden">
                        <div className="w-full h-full flex flex-col">
                            {/* Invoice content with scrollable area */}
                            <div className="flex-grow overflow-hidden">
                                <div
                                    ref={targetRef}
                                    className="w-full h-full overflow-y-auto p-3 sm:p-4 lg:p-6 bg-white"
                                >
                                    {/* Header with logo and company info */}
                                    <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
                                        {/* Company logo */}
                                        <div className="flex-shrink-0">
                                            {isLogoLoading ? (
                                                <div className="w-20 h-20 bg-gray-200 animate-pulse rounded-full" />
                                            ) : companyLogo ? (
                                                <img
                                                    id='company-logo'
                                                    src={companyLogo}
                                                    alt="Company Logo"
                                                    className="max-w-[150px] max-h-[80px] object-contain"
                                                />
                                            ) : (
                                                <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center text-gray-500">
                                                    No Logo
                                                </div>
                                            )}
                                        </div>

                                        {/* Company details */}
                                        <div className="flex text-right flex-col justify-center gap-1">
                                            <div className="font-medium text-sm sm:text-base">{companyName}</div>
                                            <div className="font-medium text-sm sm:text-base">{companyEmail}</div>
                                            <div className="font-medium text-sm sm:text-base">{companyAddress}</div>
                                        </div>
                                    </div>

                                    {/* Invoice Number and Date */}
                                    <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
                                        <div className="flex flex-col text-sm sm:text-base gap-1">
                                            <div>
                                                <span className="font-medium">GST IN No: </span>{gstNumber}
                                            </div>
                                            <div>
                                                <span className="font-medium">Receipt No: </span>{invoiceNumber}
                                            </div>
                                        </div>

                                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black">Receipt</h1>

                                        <div className="text-sm sm:text-base">
                                            <span className="font-medium">Date: </span>{invoiceDate}
                                        </div>
                                    </div>

                                    {/* Billing Information */}
                                    <div className="mb-6">
                                        <div className="bg-blue-100 p-2 mb-2 font-medium text-sm sm:text-base">
                                            BILLING INFORMATION
                                        </div>

                                        <div className="text-sm sm:text-base mb-2">
                                            <span className="font-medium">Name: </span>{salutation} {firstName}
                                        </div>

                                        {address && (
                                            <div className="text-sm sm:text-base mb-2 break-words">
                                                <span className="font-medium">Address: </span>{address}
                                            </div>
                                        )}

                                        <div className="flex flex-col sm:flex-row gap-2 text-sm sm:text-base">
                                            {mobileNumber && (
                                                <div>
                                                    <span className="font-medium">Phone: </span>{mobileNumber}
                                                </div>
                                            )}
                                            {email && (
                                                <div className="break-words">
                                                    <span className="font-medium">Email: </span>{email}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Invoice Table */}
                                    <div className="mb-6 overflow-x-auto">
                                        <table className="w-full min-w-[500px] border-collapse text-xs sm:text-sm">
                                            <thead className="bg-blue-100">
                                                <tr>
                                                    <th className="border border-gray-300 p-2 text-left w-1/2 font-medium">DESCRIPTION</th>
                                                    <th className="border border-gray-300 p-2 text-left font-medium">QUANTITY</th>
                                                    <th className="border border-gray-300 p-2 text-left font-medium">COST</th>
                                                    <th className="border border-gray-300 p-2 text-left font-medium">TAX (%)</th>
                                                    <th className="border border-gray-300 p-2 text-left font-medium w-1/6">
                                                        AMOUNT <span className="text-red-500 text-xs">(incl. tax)</span>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {table.map((itm, i) => (
                                                    <tr key={i}>
                                                        <td className="border border-gray-300 p-2 break-words">{itm?.itemDetails}</td>
                                                        <td className="border border-gray-300 p-2">{itm?.quantity}</td>
                                                        <td className="border border-gray-300 p-2">{itm?.rate}</td>
                                                        <td className="border border-gray-300 p-2">
                                                            {(Math.floor((Number(itm?.amount || 0) - (Number(itm?.quantity || 0) * Number(itm?.rate || 0))) * 100) / 100).toFixed(2)}
                                                            <span className="text-gray-500 text-xs font-medium ml-1">{itm?.tax ? `(${itm?.tax} %)` : ""}</span>
                                                        </td>
                                                        <td className="border border-gray-300 p-2">{itm?.amount}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Notes and Total */}
                                    <div className="flex flex-col md:flex-row gap-4 mb-6">
                                        <div className="w-full md:w-1/2 bg-blue-100 p-3 rounded">
                                            <div className="font-medium mb-1 text-sm sm:text-base">Notes:</div>
                                            <div className="text-xs sm:text-sm break-words">{notes}</div>
                                        </div>

                                        <div className="w-full md:w-1/2">
                                            <div className="flex justify-between items-center mb-2">
                                                <div className="font-medium text-sm sm:text-base">TOTAL</div>
                                                <div className="border border-gray-300 p-2 w-1/3 text-right text-sm sm:text-base">
                                                    {table.reduce((total, item) => total + parseFloat(item.amount || '0.00'), 0).toFixed(2)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Terms and Conditions */}
                                    <div className="mt-6">
                                        <div className="bg-blue-100 p-2 mb-2 font-medium text-sm sm:text-base">
                                            TERMS AND CONDITIONS
                                        </div>
                                        <div className="text-xs sm:text-sm break-words">
                                            {termsAndConditions.map((term, index) => (
                                                <div key={index} className="mb-1">
                                                    <span className="font-medium">{index + 1}: </span>{term}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Contact Info */}
                                    <div className="mt-6 md:mt-8 text-gray-600">
                                        {companyEmail && (
                                            <div className="flex items-center mb-2 text-xs md:text-sm">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                </svg>
                                                <span>{companyEmail}</span>
                                            </div>
                                        )}
                                        {companyNumber && (
                                            <div className="flex items-center text-xs md:text-sm">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                </svg>
                                                <span>{companyNumber}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Footer */}
                                    <div className="text-right mt-6 text-xs text-gray-500">
                                        <a href="https://crm.scholarcred.com" className="text-blue-500">
                                            © Scholarcred.com
                                        </a>
                                    </div>
                                </div>

                            </div>

                            {/* Close button */}
                            <button
                                className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
                                onClick={() => handleCLose()}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                            <hr className=" w-full" style={{ border: "none", height: "2px", background: "linear-gradient(to right, #FFFFFF 0%, #72AAD2 33%, #5E9DCB 66%, #FFFFFF 100%)", }} />
                            {/* Action buttons */}
                            <div className="flex flex-col sm:flex-row justify-end gap-3 mt-4">
                                <button
                                    className="px-3 py-2 text-white font-semibold rounded-full flex items-center justify-center gap-2 disabled:opacity-70"
                                    style={{
                                        background: 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)',
                                        cursor: isSending ? 'not-allowed' : 'pointer'
                                    }}
                                    onClick={() => sendInvoiceEmail()}
                                    disabled={isSending}
                                >
                                    {isSending ? (
                                        <>
                                            <span style={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: '500', lineHeight: '20px' }}>
                                                Sending...
                                            </span>
                                            <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        </>
                                    ) : (
                                        <>
                                            <span style={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: '500', lineHeight: '20px' }}>
                                                Send Invoice
                                            </span>
                                            <img src={`${process.env.PUBLIC_URL}/images/desktop/staffadd.svg`} alt="Add Staff Icon" className="w-4 h-4" />
                                        </>
                                    )}
                                </button>
                                <button
                                    className="px-3 py-2 text-white font-semibold rounded-full flex items-center justify-center gap-2 disabled:opacity-70"
                                    style={{
                                        background: 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)',
                                        cursor: isDownloading ? 'not-allowed' : 'pointer'
                                    }}
                                    onClick={() => handleDownload()}
                                    disabled={isDownloading}
                                >
                                    {isDownloading ? (
                                        <>
                                            <span style={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: '500', lineHeight: '20px' }}>
                                                Downloading...
                                            </span>
                                            <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        </>
                                    ) : (
                                        <>
                                            <span style={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: '500', lineHeight: '20px' }}>
                                                Download PDF
                                            </span>
                                            <img src={`${process.env.PUBLIC_URL}/images/desktop/staffadd.svg`} alt="Add Staff Icon" className="w-4 h-4" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {settingsModalOpen && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-4 md:p-6 lg:px-10 rounded-3xl w-full max-w-4xl mx-auto h-[calc(100vh-130px)] relative overflow-hidden">
                        <div className="w-full h-full flex flex-col overflow-y-auto">
                            <div className="flex justify-between items-center mb-4">
                                <h4 className="font-montserrat text-lg text-gray-800">Settings</h4>
                                <button
                                    className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
                                    onClick={() => setSettingsModalOpen(false)}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div className="flex flex-col gap-3">
                                <div className="flex flex-col gap-2">
                                    <label htmlFor="companyName" className={labelClass}>Company Name</label>
                                    <input type="text" id="companyName" value={companyName as string} placeholder="Enter Company Name" className={inputClass} style={gradientBorderStyles} onChange={(e) => setCompanyName(e.target.value)} />
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label htmlFor="companyEmail" className={labelClass}>Company Email</label>
                                    <input type="text" id="companyEmail" value={companyEmail as string} placeholder="Enter Company Email" className={inputClass} style={gradientBorderStyles} onChange={(e) => setCompanyEmail(e.target.value)} />
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label htmlFor="companyNumber" className={labelClass}>Company Number</label>
                                    <input type="text" id="companyNumber" value={companyNumber as string} placeholder="Enter Company Number" className={inputClass} style={gradientBorderStyles} onChange={(e) => setCompanyNumber(e.target.value)} />
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label htmlFor="invoiceNumberPrefix" className={labelClass}>Invoice Number Prefix</label>
                                    <input type="text" id="invoiceNumberPrefix" value={invoiceNumberPrefix as string} placeholder="Enter Invoice Number Prefix" className={inputClass} style={gradientBorderStyles} onChange={(e) => setInvoiceNumberPrefix(e.target.value)} />
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label htmlFor='gstNumber' className={labelClass}>GST Number</label>
                                    <input type="text" id="gstNumber" value={gstNumber as string} placeholder="Enter GST Number" className={inputClass} style={gradientBorderStyles} onChange={(e) => setGstNumber(e.target.value)} />
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label htmlFor="companyAddress" className={labelClass}>Company Address</label>
                                    <textarea
                                        id="companyAddress"
                                        rows={4}
                                        value={companyAddress as string}
                                        className={`${inputClass} w-[80%] h-22 resize-none`}
                                        style={gradientBorderStyles}
                                        placeholder="Enter Company Address"
                                        onChange={(e) => setCompanyAddress(e.target.value)}
                                    />
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label htmlFor="termsAndConditions" className={labelClass}>Terms and Conditons</label>
                                    {termsAndConditions && termsAndConditions?.map((term, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <input
                                                type="text"
                                                value={term}
                                                className={`${inputClass} w-[80%]`}
                                                style={gradientBorderStyles}
                                                placeholder={`Enter Term ${index + 1}`}
                                                onChange={(e) => handleTermsChange(index, e.target.value)}
                                            />
                                            <button
                                                className="px-2 py-1 text-red-500 hover:text-red-700"
                                                onClick={() => handleRemoveTerm(index)}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        className="px-3 py-2 text-blue-500 hover:text-blue-700"
                                        onClick={handleAddTerm}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        Add Term
                                    </button>
                                </div>
                            </div>
                            <hr className=" w-full" style={{ border: "none", height: "2px", background: "linear-gradient(to right, #FFFFFF 0%, #72AAD2 33%, #5E9DCB 66%, #FFFFFF 100%)", }} />
                            {/* Action buttons */}
                            <div className="flex flex-col sm:flex-row justify-end gap-3 mt-4">
                                {settingsLoading ? (
                                    <div className="flex items-center">
                                        <svg className="animate-spin h-5 w-5 mr-3 text-blue-500" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v2a6 6 0 100 12v2a8 8 0 01-8-8z"></path>
                                        </svg>
                                        <span>Loading...</span>
                                    </div>
                                ) : (
                                    <button
                                        className="px-3 py-2 text-white font-semibold rounded-full flex items-center justify-center gap-2"
                                        style={{ background: 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)', }}
                                        onClick={() => handleSettingsSave()}
                                    >
                                        <span style={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: '500', lineHeight: '20px', }}>
                                            Save Details
                                        </span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {openEmailModal && (
                <EmailModal 
                    selectedStudent={{ id: leadId, name: firstName, phone: mobileNumber }} 
                    isOpen={openEmailModal} 
                    onClose={() => {
                        setOpenEmailModal(false);
                        // Note: The EmailModal handles its own success/failure messages
                        // The modal closes automatically after successful email sending
                    }} 
                    subject={`Receipt ${invoiceNumber}`} 
                    body='Invoice attached' 
                    files={pdfDoc} 
                />
            )}

            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={isErrorToast}
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}

        </div>
    );
};

export default Invoices
